import { useEffect, useMemo, useState } from 'react'
import Styles from './PostPreviewHeader.module.scss'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { LinkAngled, Wrench } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { Spinner } from 'common/Spinner/Spinner'
import { BlogPostOutputTour } from 'components/ProductTour/Tours/BlogPostOutputTour'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useOpenIntegrations } from 'hooks/useOpenIntegrations'
import { useRootStore } from 'stores/RootStore'

import { useAuthedArticle } from '../hooks'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { useCalendarItem } from 'screens/Calendar/FullScreenPreview/useCalendarItem'
import { CalendarFilters } from 'screens/Calendar/types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { AlmanacFileSummary } from 'types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Can } from 'components/Can'
import { schemas } from 'components/Wizards/schemas/schemas'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import cx from 'classnames'
import { PostingOnMenu } from 'screens/Calendar/FullScreenPreview/Toolbar/PostingOnMenu'
import { DesignChannel } from 'components/NewFileModal/Types'
import { StatusTag } from 'screens/FinderV2/components/List/ListRow'

const createFallbackCalendarItem = (
  summary: AlmanacFileSummary,
  channel?: ChannelType | DesignChannel,
): AlmanacFileCalendarItem => ({
  key: summary.id,
  title: summary.title,
  post_at: summary.post_at || '',
  image_url: summary.preview_url || '',
  owner: undefined,
  emoji: summary.emoji || undefined,
  channel,
  asset_type: summary.asset_type,
  post_url: summary.url,
  status: summary.post_at ? PostStatusPropertyValue.AutoPostScheduled : PostStatusPropertyValue.Draft,
  duration: summary.duration,
  scheduled_post_id: summary.scheduled_post_id,
  scheduled_post_provider: undefined,
  is_posting: false,
  slug: summary.seo_string,
  postable: true,
  content: summary.content as string | undefined,
  image_url_generating: false,
  is_generating: false,
  created_at: summary.created_at,
  updated_at: summary.created_at,
  external_integration_id: undefined,
  external_integration: undefined,
  parent_id: summary.parent?.id,
  design_content: undefined,
  width: summary.width,
  height: summary.height,
  elements: undefined,
  cross_post_group_id: summary.cross_post_group_id ?? null,
  viewed_at: undefined,
  discarded: undefined,
  scheduled_post_targets: undefined,
})

export const PostPreviewHeaderV2 = () => {
  const articleMeta = useCurrentArticleMeta()
  const { isChannelPostable } = useIsChannelPostable()
  const { rightRailPanelStore } = useRootStore()
  const isBlog = articleMeta?.channel === ChannelType.BlogPost

  const almanacFileSummary = articleMeta?.almanac_file as AlmanacFileSummary

  const filters = useMemo<CalendarFilters>(() => ({}), [])
  const startDate = almanacFileSummary.post_at ?? ''

  const { data: calendarItemResponse, isLoading: isCalendarItemLoading } = useCalendarItem(
    {
      id: almanacFileSummary.id,
      filters,
      startDate,
    },
    {
      enabled: !!almanacFileSummary.id,
    },
  )

  const [calendarItem, setCalendarItem] = useState<AlmanacFileCalendarItem>(
    createFallbackCalendarItem(almanacFileSummary as AlmanacFileSummary, articleMeta?.channel),
  )
  const [isPostingMenuLoading, setIsPostingMenuLoading] = useState(false)

  useEffect(() => {
    setCalendarItem(createFallbackCalendarItem(almanacFileSummary as AlmanacFileSummary, articleMeta?.channel))
  }, [almanacFileSummary, articleMeta?.channel])

  useEffect(() => {
    if (calendarItemResponse?.almanac_file) {
      setCalendarItem(calendarItemResponse.almanac_file)
    }
  }, [calendarItemResponse])

  const scheduledPostId = calendarItem?.scheduled_post_id
  const { data: scheduledPost } = useScheduledPost(
    { id: scheduledPostId ?? undefined },
    {
      enabled: !!scheduledPostId,
    },
  )

  // Convert AlmanacFileCalendarItem to AlmanacFileShowItem for PostingOnMenu
  const almanacFileShowItem: AlmanacFileShowItem | null = useMemo(() => {
    if (!scheduledPost || !calendarItem) return null

    return {
      key: calendarItem.key,
      title: calendarItem.title,
      post_at: calendarItem.post_at,
      created_at: calendarItem.created_at,
      updated_at: calendarItem.updated_at,
      viewed_at: calendarItem.viewed_at ?? null,
      post_status: (calendarItem.status as PostStatusPropertyValue) ?? null,
      asset_type: calendarItem.asset_type ?? null,
      channel: calendarItem.channel ?? null,
      image_url: calendarItem.image_url,
      duration: calendarItem.duration ?? null,
      post_url: calendarItem.post_url ?? null,
      image_url_generating: calendarItem.image_url_generating ?? false,
      scheduled_post_targets: calendarItem.scheduled_post_targets ?? [],
      emoji: calendarItem.emoji ?? null,
      channel_emoji: calendarItem.channel_emoji ?? null,
      parent_id: calendarItem.parent_id ?? null,
      external_integration_id: calendarItem.external_integration_id ?? null,
      view_metadata: null,
      design_content: calendarItem.design_content ?? null,
      content: calendarItem.content ?? null,
      width: calendarItem.width ?? null,
      height: calendarItem.height ?? null,
      did_generation_fail: calendarItem.did_generation_fail ?? false,
      is_generating: calendarItem.is_generating,
      scheduled_post: scheduledPost,
      previous_file_key: null,
      next_file_key: null,
      elements: calendarItem.elements ?? [],
      reviewed_at: null,
      is_design_blank: calendarItem.is_design_blank ?? false,
      is_ai_generated_video: calendarItem.is_ai_generated_video ?? false,
    }
  }, [calendarItem, scheduledPost])

  const goHighLevelEnabled = useFlipperFlag('go_high_level_integration')
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()

  const blogPlatformBranding = getBlogPlatformBrandingWithOnboardingSelection({
    channel: articleMeta?.channel as ChannelType | DesignChannel,
    externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
    selectedBlogPlatform: brandPlan?.selected_blog_platform,
    featureFlags: {
      goHighLevelEnabled,
      wixEnabled,
      blogPlatformOnboardingBrandingEnabled,
    },
  })

  const openIntegrations = useOpenIntegrations()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const authedArticle = useAuthedArticle(almanacFileSummary)

  const postAt = calendarItem?.post_at
  const isPosted = calendarItem?.status === PostStatusPropertyValue.Posted
  const postUrl = scheduledPost?.post_url || calendarItem?.post_url

  const title = useMemo(() => {
    if (
      isChannelPostable(articleMeta?.channel as ChannelType | DesignChannel) &&
      articleMeta?.channel !== ChannelType.BlogPost
    ) {
      return 'Post Preview'
    }
    if (schemas[articleMeta?.channel as ChannelType]) {
      return (schemas[articleMeta?.channel as ChannelType].meta()?.title as string) || 'Document'
    }
    return 'Document'
  }, [articleMeta?.channel, isChannelPostable])

  const handleDelete = () => {
    setCalendarItem((prev) =>
      prev
        ? {
            ...prev,
            post_at: '',
            status: PostStatusPropertyValue.Draft,
          }
        : prev,
    )
  }

  const handleUpdateAlmanacFile = (updated: AlmanacFileShowItem) => {
    // Update the calendar item state with the relevant properties from the ShowItem
    setCalendarItem((prev) => ({
      ...prev,
      post_at: updated.post_at || '',
      status: updated.post_status || undefined,
      scheduled_post_targets: updated.scheduled_post_targets || undefined,
    }))
  }

  const handleConfigureMetadata = () => {
    rightRailPanelStore.show('blog_metadata')
  }

  const isLoading = isCalendarItemLoading || isPostingMenuLoading

  return (
    <div className={Styles.headerContainer}>
      <div className={Styles.header}>
        <div className={Styles.headerLeft}>
          <ChannelIcon
            channel={articleMeta?.channel as ChannelType | DesignChannel}
            size={20}
            blogPlatform={blogPlatformBranding}
          />
          <Heading level={5} color="var(--dark-80)">
            {title}
          </Heading>
          {postAt && (
            <div className={Styles.timestamp}>
              <StatusTag status={calendarItem.status ?? undefined} />
              {isPosted && postUrl && (
                <ButtonLink to={postUrl} target="_blank" size="sm" variant="ghost">
                  View Post
                </ButtonLink>
              )}
            </div>
          )}
        </div>
        {isLoading ? (
          <Spinner />
        ) : (
          <div className={cx(Styles.headerRight, Styles.actions, 'blog-post-output-tour-3')}>
            <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
              <BlogPostOutputTour eligible />
            </Can>
            {!connectedBlogIntegration && (
              <Button variant="secondary" size="sm" frontIcon={LinkAngled} onPress={openIntegrations}>
                Connect to a blog
              </Button>
            )}
            {isBlog && (
              <Button variant="secondary" size="sm" frontIcon={Wrench} onPress={handleConfigureMetadata}>
                Configure Metadata
              </Button>
            )}
            {almanacFileShowItem && (
              <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
                <PostingOnMenu
                  almanacFile={almanacFileShowItem}
                  isLoading={isPostingMenuLoading}
                  setIsLoading={setIsPostingMenuLoading}
                  onDelete={handleDelete}
                  filters={filters}
                  fileChannel={articleMeta?.channel as ChannelType | DesignChannel}
                  startDate={calendarItem.post_at}
                  onUpdateAlmanacFile={handleUpdateAlmanacFile}
                  buttonVariant="secondary"
                  buttonSize="sm"
                  buttonFullWidth={false}
                />
              </Can>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
